import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import VideoPlayer from './VideoPlayer';
import Button from '../../../components/ui/Button';



const InstituteInfo = ({ institute, videoUrls, resources, applicationLink }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  const [downloadProgress, setDownloadProgress] = useState({});

  const tabs = [
    { id: 'about', label: 'About', icon: 'Info' },
    { id: 'videos', label: 'Videos', icon: 'Video' },
    { id: 'courses', label: 'Courses', icon: 'BookOpen' },
    { id: 'downloads', label: 'Download Resources', icon: 'Download' },
    { id: 'fees', label: 'Fees', icon: 'IndianRupee' },
    { id: 'contact', label: 'Contact', icon: 'Phone' }
  ];

  const handleDownload = (resourceId, fileName) => {
    setDownloadProgress({ ...downloadProgress, [resourceId]: 0 });

    const interval = setInterval(() => {
      setDownloadProgress((prev) => {
        const currentProgress = prev?.[resourceId] || 0;
        if (currentProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setDownloadProgress((p) => {
              const newProgress = { ...p };
              delete newProgress?.[resourceId];
              return newProgress;
            });
          }, 1000);
          return prev;
        }
        return { ...prev, [resourceId]: currentProgress + 10 };
      });
    }, 200);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <div className="space-y-4 md:space-y-5 lg:space-y-6">
            <div>
              <h3 className="text-base md:text-lg lg:text-xl font-heading font-semibold text-foreground mb-3">
                About {institute?.name}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {institute?.about}
              </p>
            </div>
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">
                Accreditations & Rankings
              </h4>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {institute?.accreditations?.map((acc, index) => (
                  <div
                    key={index}
                    className="px-3 py-1.5 md:px-4 md:py-2 bg-primary/10 text-primary rounded-lg text-xs md:text-sm font-medium"
                  >
                    {acc}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">
                Campus Facilities
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                {institute?.facilities?.map((facility, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground"
                  >
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span>{facility}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'videos':
        return (
          <div className="space-y-4">
            {videoUrls?.length > 0 ? (
              videoUrls?.map((video, index) => (
                <VideoPlayer
                  key={index}
                  videoUrl={video?.url}
                  title={video?.title || `${institute?.name} - Campus Video ${index + 1}`}
                  duration={596}
                />
              ))
            ) : (
              <div className="bg-muted border border-border rounded-lg p-8 text-center">
                <Icon name="Video" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Videos Available</h3>
                <p className="text-muted-foreground">This institute hasn't uploaded any videos yet.</p>
              </div>
            )}
          </div>
        );

      case 'courses': console.log('🎓 [COURSES TAB DEBUG] applicationLink prop:', applicationLink);
        console.log('🎓 [COURSES TAB DEBUG] applicationLink type:', typeof applicationLink);
        console.log('🎓 [COURSES TAB DEBUG] applicationLink truthiness:', !!applicationLink);
        console.log('🎓 [COURSES TAB DEBUG] Number of courses:', institute?.courses?.length);
        
        return (
          <div className="space-y-3 md:space-y-4">
            {institute?.courses?.map((course, index) => {
              console.log(`🎓 [COURSE ${index}] Rendering course:`, course?.name);
              console.log(`🎓 [COURSE ${index}] Will show Apply Now button:`, !!applicationLink);
              
              return (
                <div
                  key={index}
                  className="p-4 md:p-5 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="text-sm md:text-base font-semibold text-foreground">
                      {course?.name}
                    </h4>
                    <span className="px-2 py-1 bg-primary text-primary-foreground text-xs rounded-lg whitespace-nowrap">
                      {course?.duration}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mb-3">
                    {course?.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {course?.specializations?.map((spec, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-background text-foreground text-xs rounded border border-border"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                  {/* Apply Now button - prominent and clearly visible */}
                  {applicationLink ? (
                    <a
                      href={applicationLink?.url || applicationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-primary to-primary/90 text-primary-foreground hover:from-primary/90 hover:to-primary font-bold shadow-lg hover:shadow-xl transition-all duration-300 px-6 py-3.5 rounded-lg mt-4 text-base md:text-lg border-2 border-primary-foreground/20 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <Icon name="ExternalLink" size={20} color="currentColor" />
                      <span>Apply Now</span>
                      <Icon name="ArrowRight" size={20} color="currentColor" />
                    </a>
                  ) : (
                    <div className="text-center text-sm text-muted-foreground mt-4 p-3 bg-background rounded border border-border">
                      ⚠️ No application link available
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 'downloads':
        return (
          <div className="space-y-3 md:space-y-4">
            {resources?.length > 0 ? (
              resources?.map((resource) => (
                <div
                  key={resource?.id}
                  className="p-4 md:p-5 bg-muted rounded-lg hover:bg-muted/80 transition-smooth"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={24} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {resource?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground data-text">
                        {formatFileSize(resource?.size)} • PDF
                      </p>
                    </div>
                  </div>

                  {downloadProgress?.[resource?.id] !== undefined ? (
                    <div className="space-y-2">
                      <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-200"
                          style={{ width: `${downloadProgress?.[resource?.id]}%` }}
                        />
                      </div>
                      <p className="text-xs text-center text-muted-foreground data-text">
                        {downloadProgress?.[resource?.id]}%
                      </p>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleDownload(resource?.id, resource?.name)}
                    >
                      Download
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-muted border border-border rounded-lg p-8 text-center">
                <Icon name="Download" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Resources Available</h3>
                <p className="text-muted-foreground">This institute hasn't uploaded any downloadable resources yet.</p>
              </div>
            )}
          </div>
        );

      case 'eligibility':
        return (
          <div className="space-y-4 md:space-y-5">
            {institute?.eligibility?.map((criteria, index) => (
              <div key={index} className="space-y-3">
                <h4 className="text-sm md:text-base font-semibold text-foreground">
                  {criteria?.program}
                </h4>
                <ul className="space-y-2">
                  {criteria?.requirements?.map((req, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 text-xs md:text-sm text-muted-foreground"
                    >
                      <Icon
                        name="CheckCircle"
                        size={16}
                        color="var(--color-primary)"
                        className="flex-shrink-0 mt-0.5"
                      />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );

      case 'fees':
        return (
          <div className="space-y-4 md:space-y-5">
            {institute?.fees?.map((fee, index) => (
              <div
                key={index}
                className="p-4 md:p-5 bg-muted rounded-lg"
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm md:text-base font-semibold text-foreground">
                    {fee?.program}
                  </h4>
                  <span className="text-lg md:text-xl lg:text-2xl font-heading font-bold text-primary data-text">
                    ₹{fee?.amount?.toLocaleString()}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span className="text-muted-foreground">Per Year</span>
                    <span className="font-medium text-foreground data-text">
                      ₹{fee?.perYear?.toLocaleString()}
                    </span>
                  </div>
                  {fee?.scholarships && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-success">
                        <Icon name="Award" size={14} className="inline mr-1" />
                        Scholarships available up to {fee?.scholarships}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-4 md:space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="MapPin" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-medium text-foreground mb-1">
                      Address
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {institute?.contact?.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Phone" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-medium text-foreground mb-1">
                      Phone
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground data-text">
                      {institute?.contact?.phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-medium text-foreground mb-1">
                      Email
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {institute?.contact?.email}
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-48 md:h-64 rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={institute?.name}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${institute?.contact?.coordinates?.lat},${institute?.contact?.coordinates?.lng}&z=14&output=embed`}
                  className="border-0"
                />
              </div>
            </div>
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">
                Office Hours
              </h4>
              <div className="space-y-2">
                {institute?.contact?.hours?.map((hour, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-xs md:text-sm"
                  >
                    <span className="text-muted-foreground">{hour?.days}</span>
                    <span className="font-medium text-foreground data-text">
                      {hour?.time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden">
      <div className="border-b border-border overflow-x-auto">
        <div className="flex gap-1 p-2 md:p-3 min-w-max">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-lg transition-smooth flex-shrink-0 ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon
                name={tab?.icon}
                size={18}
                color={activeTab === tab?.id ? 'var(--color-primary-foreground)' : 'currentColor'}
              />
              <span className="text-sm md:text-base font-medium whitespace-nowrap">
                {tab?.label}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-center gap-2 mb-4 md:mb-5">
          <Icon name="Building2" size={20} color="var(--color-primary)" />
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            About {institute?.name}
          </h2>
        </div>

        <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 leading-relaxed">
          {institute?.description}
        </p>

        {/* Add 360 Campus Tour Button */}
        <div className="mb-4 md:mb-6">
          <Button
            variant="outline"
            fullWidth
            iconName="Compass"
            iconPosition="left"
            onClick={() => navigate(`/360-campus-tour/${institute?.id}`)}
            className="border-primary text-primary hover:bg-primary/10"
          >
            Take 360° Campus Tour
          </Button>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default InstituteInfo;